/**
 * ie-payload.ts — normaliza el payload crudo de un informe IE a la forma tipada
 * IePayload, rellenando con valores vacíos las secciones ausentes.
 *
 * El editor mantiene SIEMPRE el payload completo en estado; al guardar se envía
 * entero vía updateInformePayload (no dependemos de merge en el backend).
 */
import type {
  IeDatosGenerales,
  IeEquipo,
  IeFoto,
  IePayload,
  IeProblema,
} from "../../types.js";
import { DEFAULT_CARGO } from "../paso-datos-generales-helpers.js";

/**
 * Títulos por defecto del panel fotográfico de un IE recién creado. Son
 * editables (el técnico puede renombrarlos) y los slots pueden agregarse/quitarse.
 * Solo se siembran cuando el informe NO tiene aún la clave `panelFotografico`.
 */
export const PANEL_DEFAULT_TITULOS: readonly string[] = [
  "Presentación del equipo",
  "Sistema centralizado",
  "Dispositivo funcional principal",
  "Sistema de distribución",
  "Sistema de dosificación",
  "Sistema de alarmas",
  "Comprobación de funcionamiento",
];

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function datosGenerales(raw: unknown, clienteFallback: string): IeDatosGenerales {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const cliente = str(o["cliente"]) || clienteFallback;
  return {
    cliente,
    atencion: str(o["atencion"]),
    supervisor: str(o["supervisor"]),
    cargo: str(o["cargo"]) || DEFAULT_CARGO,
    fechaInicio: str(o["fechaInicio"]),
    fechaCierre: str(o["fechaCierre"]),
    referencia: str(o["referencia"]),
    lugar: str(o["lugar"]),
    ocCliente: str(o["ocCliente"]),
    otMpsa: str(o["otMpsa"]),
    emision: str(o["emision"]),
    anexo: str(o["anexo"]),
    revision: num(o["revision"]),
  };
}

function equipos(raw: unknown): IeEquipo[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e, i): IeEquipo => {
    const o = (e && typeof e === "object" ? e : {}) as Record<string, unknown>;
    const item = o["item"];
    return {
      item: typeof item === "number" && Number.isFinite(item) ? item : i + 1,
      equipo: str(o["equipo"]),
      marca: str(o["marca"]),
      modelo: str(o["modelo"]),
      serie: str(o["serie"]),
      designacion: str(o["designacion"]),
    };
  });
}

function panelFotografico(raw: unknown): IeFoto[] {
  // Brand-new IE: la clave aún no existe → sembramos los 7 slots por defecto.
  // Si la clave existe (incluso `[]` tras borrarlos todos), respetamos lo guardado.
  if (raw === undefined) {
    return PANEL_DEFAULT_TITULOS.map((titulo): IeFoto => ({
      titulo,
      descripcion: "",
      url: "",
    }));
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((f): IeFoto => {
    const o = (f && typeof f === "object" ? f : {}) as Record<string, unknown>;
    return {
      url: str(o["url"]),
      titulo: str(o["titulo"]),
      descripcion: str(o["descripcion"]),
    };
  });
}

function problemas(raw: unknown): IeProblema[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((p): IeProblema => {
    const o = (p && typeof p === "object" ? p : {}) as Record<string, unknown>;
    return {
      descripcion: str(o["descripcion"]),
      recomendacion: str(o["recomendacion"]),
    };
  });
}

/**
 * Restores the panel fotográfico to the 7 default slots from PANEL_DEFAULT_TITULOS.
 * Each slot has empty descripcion and url. Used by "Restaurar predeterminados" button.
 * // ponytail: maps the constant, no extra state needed.
 */
export function restaurarPredeterminados(): IeFoto[] {
  return PANEL_DEFAULT_TITULOS.map((titulo): IeFoto => ({ titulo, descripcion: "", url: "" }));
}

/** Construye un IePayload completo a partir del payload crudo del informe. */
export function toIePayload(raw: Record<string, unknown>, clienteFallback: string): IePayload {
  return {
    datosGenerales: datosGenerales(raw["datosGenerales"], clienteFallback),
    equipos: equipos(raw["equipos"]),
    // `raw["panelFotografico"]` es `undefined` solo si la clave NO está presente
    // (informe nuevo). Tras un guardado siempre existe — aunque sea `[]`.
    panelFotografico: panelFotografico(raw["panelFotografico"]),
    problemas: problemas(raw["problemas"]),
  };
}
